export default class StickyThemeNavigation {
  constructor(id = "denhaag-sticky-theme-navigation") {
    this.id = id;
    this.stickyNavigation = document.getElementById( id );

    this.links = [];
    this.list = null;
    this.listItems = [];

    this.scrollContent = null;
    this.buttonContainer = null;
    this.btn = {
      prev: null,
      next: null
    };

    this.scrollOffset = 0;
  }

  initialize() {
    if (!this.stickyNavigation) {
      return;
    }

    this.scrollOffset = this.getCSSVariable( '--denhaag-theme-navigation-gradient-width' ) + this.getCSSVariable( '--denhaag-space-xl' );

    this.buttonContainer = this.stickyNavigation.querySelector( ".denhaag-scroll-buttons" );
    this.scrollContent = this.stickyNavigation.querySelector( ".denhaag-scroll-content" );
    this.btn = {
      prev: this.buttonContainer.querySelector( ".denhaag-scroll-btn--left" ),
      next: this.buttonContainer.querySelector( ".denhaag-scroll-btn--right" )
    };
    this.links = this.stickyNavigation.getElementsByTagName( 'A' );
    this.list = document.getElementById( 'denhaag-theme-navigation-list' );
    this.listItems = this.list.getElementsByTagName( 'LI' ) || [];

    // Set roving tabindex: first link or current page gets tabindex="0"
    const currentLink = this.stickyNavigation.querySelector( '[aria-current="page"]' );
    [...this.links].forEach( (link, index) => {
      if (currentLink && link === currentLink) {
        link.tabIndex = 0;
      } else if (!currentLink && index === 0) {
        link.tabIndex = 0;
      } else {
        link.tabIndex = -1;
      }
    } );

    // Create scroll buttons.
    this.createScrollButtons();

    // Check for overflow on load and resize.
    this.checkOverflow();

    // Update button states on scroll.
    this.scrollContent.addEventListener( "scroll", () => this.updateButtonStates() );
  }

  /**
   * Get a CSS variable value and parse it as an integer.
   * @param {string} varName The name of the CSS variable (e.g., '--my-variable').
   * @param {number} defaultValue The default value to return if the variable is not found or invalid.
   * @return {number}
   */
  getCSSVariable(varName, defaultValue = 0) {
    if (typeof varName !== 'string' || !this.stickyNavigation) {
      return defaultValue;
    }

    const value = getComputedStyle( this.stickyNavigation ).getPropertyValue( varName );
    return value ? parseInt( value.trim(), 10 ) : defaultValue;
  }

  keyboardEvents() {
    let newLink;
    [...this.links]?.forEach( ((link, index) => link.addEventListener( 'keydown', (e) => {
      const {code} = e;

      switch (code) {
        case 'ArrowLeft':
          e.preventDefault();
          newLink = this.links[0 >= index ? this.links.length - 1 : index - 1];
          this.setFocus( newLink );
          this.setTabIndex( newLink, 0 );
          this.setTabIndex( link, -1 );

          break;
        case 'ArrowRight':
          e.preventDefault();
          newLink = this.links[index >= this.links.length - 1 ? 0 : index + 1];
          this.setFocus( newLink );
          this.setTabIndex( newLink, 0 );
          this.setTabIndex( link, -1 );

          break;
        case 'Enter':
        case 'Space':
          e.preventDefault();
          // Scroll into view if outside of view.
          e.target.click();
          break;
      }

    } )) );
  }

  // Set stuck detection if scroll-state is not supported
  detectIfSubNavStuck() {
    const stickyEl = this.stickyNavigation;
    if (!stickyEl) {
      return;
    }

    new IntersectionObserver( ([entry]) => entry.boundingClientRect.top <= 0
      ? stickyEl.classList.add( "is-stuck" )
      : stickyEl.classList.remove( "is-stuck" ) ).observe( stickyEl );
  }

  /**
   * Fired on the window resize event.
   */
  resize() {
    this.checkOverflow();
  }

  createScrollButtons() {
    // Add event listeners
    this.btn.prev.addEventListener( "click", () => this.scrollToEl( 'left' ) );
    this.btn.next.addEventListener( "click", () => this.scrollToEl( 'right' ) );
  }

  /**
   * Check if the scroll content has overflow and update button visibility.
   */
  checkOverflow() {
    if (!this.stickyNavigation || !this.scrollContent) {
      return;
    }

    const hasOverflow = this.scrollContent.scrollWidth > this.scrollContent.clientWidth;
    hasOverflow
      ? this.scrollContent.classList.add( "has-overflow" )
      : this.scrollContent.classList.remove( "has-overflow" );
    this.updateButtonStates();
  }

  /**
   * Update the disabled state of the scroll buttons based on the current scroll position.
   */
  updateButtonStates() {
    const {scrollLeft, scrollWidth, clientWidth} = this.scrollContent;

    const leftDisabled = scrollLeft <= 0;
    const rightDisabled = scrollLeft >= scrollWidth - clientWidth - 1;

    this.btn.prev.disabled = leftDisabled;
    this.btn.next.disabled = rightDisabled;

    const {sticky_subnav} = window.wp_variables || {};

    // Optional: Update aria-label to reflect state
    this.btn.prev.setAttribute( 'aria-label',
      leftDisabled
        ? sticky_subnav.labels.prev.inactive || 'At start of navigation'
        : sticky_subnav.labels.prev.active || 'Scroll navigation left'
    );
    this.btn.next.setAttribute( 'aria-label',
      rightDisabled
        ? sticky_subnav.labels.next.inactive || 'At end of navigation'
        : sticky_subnav.labels.next.active || 'Scroll navigation right'
    );
  }

  /**
   * Set the tabindex attribute on an element.
   *
   * @param {HTMLElement} element The HTML element to set the tabindex on.
   * @param {number} tabIndex The tabindex value to set. Use 0 for focusable, -1 for not focusable.
   */
  setTabIndex(element, tabIndex) {
    if (!element) {
      return;
    }

    if (0 === tabIndex) {
      element.removeAttribute( 'tabindex' );
    }

    element.tabIndex = tabIndex;
  }

  /**
   * Find the next fully hidden item in the specified direction.
   * @param {string} direction - 'left' or 'right'
   * @param {boolean} isPartiallyVisible - If true, consider partially visible items as well.
   * @returns {HTMLElement|null} The next hidden item or null
   */
  getNextHiddenItem(direction = 'right', isPartiallyVisible = false) {
    const containerRect = this.scrollContent.getBoundingClientRect();
    const visibleLeft = containerRect.left + this.scrollOffset;
    const visibleRight = containerRect.right - this.scrollOffset;

    switch (direction) {
      case 'right':
        // NOTICE: Looping forwards through the list.
        for (let i = 0; i < this.listItems.length; i++) {
          if (isPartiallyVisible) {
            const {behindRight} = this.isElementBehindOffset( this.listItems[i] );
            if (behindRight) {
              return this.listItems[i];
            }
          }

          const itemRect = this.listItems[i].getBoundingClientRect();
          if (itemRect.left >= visibleRight) {
            return this.listItems[i];
          }
        }
        return null;
      case 'left':
        // NOTICE: Looping backwards through the list.
        for (let i = this.listItems.length - 1; i >= 0; i--) {
          if (isPartiallyVisible) {
            const {behindLeft} = this.isElementBehindOffset( this.listItems[i] );
            if (behindLeft) {
              return this.listItems[i];
            }
          }

          const itemRect = this.listItems[i].getBoundingClientRect();
          if (itemRect.right <= visibleLeft) {
            return this.listItems[i];
          }
        }

        return null;
      default:
        console.warn( 'Invalid direction specified for getNextHiddenItem:', direction );
        return null;
    }
  }

  /**
   * Scroll to the next/previous hidden element in the specified direction.
   * @param {string} direction - 'left' or 'right'
   */
  scrollToEl(direction = 'right') {
    let isPartialyVisible = true;
    let targetItem = this.getNextHiddenItem( direction, true );

    if (!targetItem) {
      targetItem = this.getNextHiddenItem( direction );
      isPartialyVisible = false;
    }


    const {scroll_status, prev, next} = window.wp_variables.sticky_subnav.labels;

    if (targetItem) {
      // If the item is only partially visible, apply an extra offset to fully reveal it.
      this.scrollTo( targetItem, isPartialyVisible ? this.scrollOffset / 2 : 0 );
      this.announceScrollStatus( (scroll_status || 'Scroll to %s').replace( '%s', targetItem.textContent.trim() ) );
    } else if (direction === 'left') {
      // Reached the start, disable button.
      this.btn.prev.disabled = true;
      this.announceScrollStatus( prev.inactive || 'Start of navigation' );
    } else {
      // Reached the end, disable button.
      this.btn.next.disabled = true;
      this.announceScrollStatus( next.inactive || 'End of navigation' );
    }
  }

  /**
   * Scroll the container to ensure the element is visible, applying an optional offset.
   * @param {HTMLElement} element The element to scroll into view.
   * @param {number} additionalOffset Additional offset in pixels to apply (default is 0).
   */
  scrollTo(element, additionalOffset = 0) {
    if (!element) {
      return;
    }

    // Check if element is behind offsets or outside visible area.
    const {behindLeft, behindRight} = this.isElementBehindOffset( element );

    const containerRect = this.scrollContent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Determine offset based on which buttons are visible.
    const hasPrevBtn = this.btn.prev && !this.btn.prev.disabled;
    const hasNextBtn = this.btn.next && !this.btn.next.disabled;

    const visibleLeft = containerRect.left + (hasPrevBtn ? this.scrollOffset : 0);
    const visibleRight = containerRect.right - (hasNextBtn ? this.scrollOffset : 0);

    // Check if element is fully visible (not behind offsets and within bounds).
    const isFullyVisible =
      !behindLeft &&
      !behindRight &&
      elementRect.left >= visibleLeft &&
      elementRect.right <= visibleRight;

    // Only scroll if the element is not fully visible.
    if (!isFullyVisible) {
      if (behindLeft) {
        // Element is cut off on the left - align it to the left edge plus offset.
        this.scrollContent.scrollLeft = element.offsetLeft - (this.scrollOffset + additionalOffset);
      } else if (behindRight) {
        // Element is cut off on the right - align it to the right edge minus offset.
        // Calculate: current scroll + how much element extends beyond visible right.
        const elementRightPosition = element.offsetLeft + element.offsetWidth;
        const visibleWidth = this.scrollContent.clientWidth - (this.scrollOffset + additionalOffset);
        this.scrollContent.scrollLeft = elementRightPosition - visibleWidth;
      } else {
        // Element is completely outside view - use default left alignment.
        this.scrollContent.scrollLeft = element.offsetLeft - (this.scrollOffset + additionalOffset);
      }
    }
  }

  /**
   * Check if an element is behind the left or right offset areas.
   *
   * @param {HTMLElement} element The element to check
   * @returns {Object} Object with `behindLeft` and `behindRight` boolean properties
   */
  isElementBehindOffset(element) {
    if (!element) {
      return {behindLeft: false, behindRight: false};
    }

    const containerRect = this.scrollContent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Check which buttons are visible/enabled.
    const hasPrevBtn = this.btn.prev && !this.btn.prev.disabled;
    const hasNextBtn = this.btn.next && !this.btn.next.disabled;

    // Calculate offset areas based on visible buttons.
    const leftOffset = hasPrevBtn ? this.scrollOffset : 0;
    const rightOffset = hasNextBtn ? this.scrollOffset : 0;

    // Define the visible area boundaries.
    const visibleLeft = containerRect.left + leftOffset;
    const visibleRight = containerRect.right - rightOffset;

    // Check if element is behind offsets.
    const behindLeft = elementRect.left < visibleLeft && elementRect.right > containerRect.left;
    const behindRight = elementRect.right > visibleRight && elementRect.left < containerRect.right;

    return {behindLeft, behindRight};
  }

  /**
   * Get all items that are currently behind offset areas.
   *
   * @returns {Object} Object with `leftItems` and `rightItems` arrays
   */
  getItemsBehindOffsets() {
    const leftItems = [];
    const rightItems = [];

    [...this.listItems]?.forEach( item => {
      const {behindLeft, behindRight} = this.isElementBehindOffset( item );

      if (behindLeft) {
        leftItems.push( item );
      }
      if (behindRight) {
        rightItems.push( item );
      }
    } );

    return {leftItems, rightItems};
  }

  /**
   * Set focus to an element.
   * When the element is not fully visible, scroll it into view with offset.
   *
   * @param {HTMLElement} element The element to focus.
   */
  setFocus(element) {
    if (!element) {
      return;
    }

    this.scrollTo( element );
    element.focus();
  }

  /**
   * Announce scroll status to screen readers via the live region.
   * @param {string} message The message to announce
   */
  announceScrollStatus(message) {
    const statusElement = document.getElementById( 'scroll-status' );
    if (statusElement) {
      statusElement.textContent = message;

      // Clear after a delay so repeated announcements work.
      setTimeout( () => statusElement.textContent = '', 1000 );
    }
  }
}
